<template>
    <div class="ui-menu" :class="classes">
        <slot name="activator" v-bind="{ isShowMenu, toggleMenu }">
            <WBIcon class="ui-menu__button"
                    :class="{ _active: isShowMenu }"
                    :name="icon"
                    @click="toggleMenu"
            />
        </slot>

        <template v-if="!isMobile">
            <transition name="dropdown" mode="out-in">
                <div v-show="isShowMenu" class="ui-menu__wrapper" :class="{ _active: isShowMenu }">
                    <div v-on-click-outside="onClickOutside" class="ui-menu__content" :class="{ _active: isShowMenu }">
                        <slot />
                    </div>
                </div>
            </transition>
        </template>

        <template v-else>
            <teleport to="#teleports">
                <transition name="fade" mode="out-in">
                    <div v-show="isShowMenu" class="ui-menu__wrapper" :class="{ _active: isShowMenu }">
                        <div v-on-click-outside="onClickOutside" class="ui-menu__content" :class="{ _active: isShowMenu }">
                            <button class="ui-menu__close" @click="hideMenu">
                                <WBIcon name="cross" />
                            </button>
                            <slot v-bind="{ isShowMenu, toggleMenu }" />
                        </div>
                    </div>
                </transition>
            </teleport>
        </template>
    </div>
</template>

<script setup lang="ts">
    import type { UiMenuProps } from './types'
    import { vOnClickOutside } from '@vueuse/components'
    import { useWindowStore } from '~/src/shared/pinia/useWindowStore'

    const props = withDefaults(defineProps<UiMenuProps>(), {
        position: 'left',
        icon: 'menu',
    })

    const windowStore = useWindowStore()
    const isMobile = computed(() => windowStore.isMobile)

    const { isShowMenu, hideMenu, toggleMenu } = useUIMenu()

    const classes = computed(() => ({
        [`_${props.position}`]: props.position,
    }))
    const lockScroll = useScrollLock(document)

    watch(() => isShowMenu.value, (value) => {
        lockScroll.value = value && isMobile.value
    })

    const close = () => {
        hideMenu()
    }

    provide('closeUiMenu', close)

    const onClickOutside = () => {
        hideMenu()
    }

    function useUIMenu() {
        const isShowMenu = ref(false)

        const hideMenu = () => {
            isShowMenu.value = false
        }

        const toggleMenu = () => {
            isShowMenu.value = !isShowMenu.value
        }

        return {
            isShowMenu,
            hideMenu,
            toggleMenu,
        }
    }
</script>

<style lang="scss" scoped>
    .ui-menu {
        $this: &;

        position: relative;
        width: fit-content;

        &__button {
            width: 24px;
            height: 24px;
            border-radius: 4px;
            cursor: pointer;

            @include hover {
                color: var(--blue-600);
            }

            &._active {
                background-color: var(--blue-50);
                color: var(--blue-600);
            }
        }

        &__wrapper {
            position: absolute;
            top: calc(100% + 8px);
            z-index: 3;

            @include respond-to(mobile) {
                position: fixed;
                top: 0;
                left: 0;
                z-index: 1000;
                width: 100%;
                height: 100%;
                border: none;
                background-color: var(--modal-overlay);
            }
        }

        &__content {
            display: flex;
            justify-content: center;
            flex-flow: column;
            min-width: 250px;
            padding: 8px;
            border-radius: 12px;
            background-color: var(--white);
            box-shadow: -2px 3px 12px 0 #0F35A533;

            @include respond-to(mobile) {
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: auto;
                padding: 36px 16px 28px;
                border-top-left-radius: 20px;
                border-top-right-radius: 20px;
                box-shadow: none;
                transform: translateY(100%);
                transition: transform $default-transition;
            }

            &._active {
                @include respond-to(mobile) {
                    transform: translateY(0);
                    transition-delay: 0.2s;
                }
            }
        }

        &__close {
            position: absolute;
            top: 16px;
            right: 16px;
            font-size: 20px;

            &:after {
                content: "";
                position: absolute;
                bottom: 0;
                left: 50%;
                display: block;
                width: 175%;
                height: 175%;
                transform: translateX(-50%);
            }
        }

        &._left {
            #{$this} {
                &__wrapper {
                    right: 0;
                }
            }
        }

        &._right {
            #{$this} {
                &__wrapper {
                    left: 0;
                }
            }
        }
    }
</style>
